/**
 * ReferralDomain - Referral Management Domain Service
 *
 * Handles all referral-related operations including referral creation,
 * claiming, tracking, and management. Extends BaseDomain to leverage
 * shared infrastructure for API calls, retry logic, and error handling.
 *
 * Features:
 * - Referral token generation and validation
 * - Referral claiming and processing
 * - Referral status tracking and updates
 * - Patient referral management
 * - Provider referral analytics
 * - Integration with StorageManager for referral persistence
 *
 * @module services/domains/ReferralDomain
 */

import { BaseDomain, BaseApiError, ApiClient, tokenManager, requestManager, getLoggerWithPrefix } from '../shared';
import { storageManager } from '@/utils/StorageManager';
import type { PaymentGuidance } from '@/types/paymentGuidance';
import { REFERRAL_STORAGE } from '@/constants/storage';
import type {
  PatientData,
  DietitianProfile,
  ReferralSubmissionResult,
  ReferralSubmissionResponse
} from '@/types/referral';

/**
 * Referral-specific error class
 */
export class ReferralApiError extends BaseApiError {
  constructor(
    message: string,
    status?: number,
    fieldErrors?: Record<string, string>,
    code?: string
  ) {
    super(message, status, fieldErrors, code);
    this.name = 'ReferralApiError';
  }
}

/**
 * Referral status enumeration
 */
export type ReferralStatus = 'pending' | 'claimed' | 'scheduled' | 'completed' | 'expired' | 'cancelled';

/**
 * Referral interface - Matches actual API response structure with snake_case fields
 */
export interface Referral {
  id: string;
  referral_token: string;
  referring_physician_id: string;
  referring_physician_name: string;
  referring_physician_email?: string;
  referring_physician_role?: string;
  dietitian_id?: string;
  dietitian_name?: string;
  dietitian_email?: string;
  dietitian_scheduling_uri?: string;
  patient_id?: string;
  patient_name?: string;
  patient_email?: string;
  date_of_birth?: string;
  patient_id_number?: string;
  patient_id_type?: string;
  phone?: string;
  email?: string;
  conditions?: string[];
  additional_notes?: string;
  status: ReferralStatus;
  claimed_by_user_id?: string;
  claimed_at?: string;
  completed_at?: string;
  expires_at: string;
  created_at: string;
  updated_at: string;
  document_count?: number;
  /** Populated on the token endpoint only (design §4.5, D4). */
  payment_guidance?: PaymentGuidance | null;
  metadata?: Record<string, unknown>;
}

/**
 * Referral creation request interface
 */
export interface CreateReferralRequest {
  patientEmail?: string;
  patientName?: string;
  notes?: string;
  expiresInDays?: number;
  metadata?: Record<string, unknown>;
}

/**
 * Phone number interface
 */
export interface PhoneNumber {
  number: string;
  country_code: string;
  iso_code: string;
}

/**
 * Referral creation response interface
 */
export interface CreateReferralResponse {
  patient_name: string;                    // Required
  date_of_birth?: string | null;          // Optional: Can be null - YYYY-MM-DD format
  patient_id_number?: string | null;      // Optional: Can be null
  patient_id_type?: 'medicare' | 'drivers_license' | 'passport' | 'healthcare_card' | 'pension_card' | 'dva_card' | 'other' | null; // Optional
  phone?: PhoneNumber | null;             // Optional: Can be null
  email?: string | null;                  // Optional: Can be null
  conditions: string[];                    // Required: at least one condition
  additional_notes?: string;               // Optional
  dietitian_id: string;                    // Required: UUID of the dietitian to refer to
}

/**
 * Referral claim request interface
 */
export interface ClaimReferralRequest {
  token: string;
  patientId?: string;
  patientEmail?: string;
  patientName?: string;
}

/**
 * Referral claim response interface
 */
export interface ClaimReferralResponse {
  status: 'OK' | 'INVALID_TOKEN' | 'ALREADY_CLAIMED' | 'EXPIRED' | 'FIELD_ERROR';
  referral?: Referral;
  formFields?: Array<{
    id: string;
    error: string;
  }>;
}

/**
 * Referral list request interface
 */
export interface ListReferralsRequest {
  status?: ReferralStatus;
  providerId?: string;
  patientId?: string;
  limit?: number;
  offset?: number;
  sortBy?: 'createdAt' | 'updatedAt' | 'expiresAt';
  sortOrder?: 'asc' | 'desc';
}

/**
 * Referral list response interface
 */
export interface ListReferralsResponse {
  referrals: Referral[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

/**
 * Referral update request interface
 */
export interface UpdateReferralRequest {
  status?: ReferralStatus;
  notes?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Referral update response interface
 */
export interface UpdateReferralResponse {
  status: 'OK' | 'NOT_FOUND' | 'FIELD_ERROR' | 'GENERAL_ERROR';
  referral?: Referral;
  formFields?: Array<{
    id: string;
    error: string;
  }>;
}

/**
 * Referral validation response interface
 */
export interface ValidateReferralResponse {
  status: 'OK' | 'INVALID_TOKEN' | 'EXPIRED' | 'ALREADY_CLAIMED';
  referral?: Referral;
  isValid: boolean;
}

/**
 * Referral document interface - Matches API response structure with snake_case fields
 */
export interface ReferralDocument {
  id: string;
  referral_id: string;
  original_filename: string;
  stored_filename: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  document_type: string;
  description?: string;
  uploaded_by_user_id: string;
  created_at: string;
  updated_at?: string;
}

/**
 * Document upload request interface
 */
export interface UploadDocumentRequest {
  referralId: string;
  fileData: string;
  filename: string;
  mimeType: string;
  description?: string;
}

/**
 * Document upload response interface
 */
export interface UploadDocumentResponse {
  status: 'OK' | 'FIELD_ERROR' | 'GENERAL_ERROR';
  document?: ReferralDocument;
  formFields?: Array<{
    id: string;
    error: string;
  }>;
}

/**
 * Documents list request interface
 */
export interface ListDocumentsRequest {
  page?: number;
  perPage?: number;
}

/**
 * Documents list response interface
 */
export interface ListDocumentsResponse {
  status: 'OK' | 'GENERAL_ERROR';
  documents: ReferralDocument[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

/**
 * Document description update request interface
 */
export interface UpdateDocumentDescriptionRequest {
  description?: string;
}

/**
 * Document description update response interface
 */
export interface UpdateDocumentDescriptionResponse {
  status: 'OK' | 'NOT_FOUND' | 'FIELD_ERROR' | 'GENERAL_ERROR';
  document?: ReferralDocument;
  formFields?: Array<{
    id: string;
    error: string;
  }>;
}

/**
 * Document deletion response interface
 */
export interface DeleteDocumentResponse {
  status: 'OK' | 'NOT_FOUND' | 'GENERAL_ERROR';
  message: string;
}

/**
 * Legacy compatibility interfaces for referral submission
 */
interface ReferralApiRequest {
  patient_name: string;
  email: string;
  conditions: string[];
  additional_notes?: string;
  dietitian_id: string;
}

/**
 * Referral domain service class
 */
export class ReferralDomain extends BaseDomain<ReferralApiError> {
  constructor() {
    const apiClient = new ApiClient<ReferralApiError>(
      (message, status, fieldErrors, code) => new ReferralApiError(message, status, fieldErrors, code)
    );

    super(apiClient, tokenManager, requestManager);
  }

  logger = getLoggerWithPrefix('ReferralDomain');

  /**
   * Create a new referral
   *
   * @param request - Referral creation request data
   * @returns Promise resolving to referral creation response
   */
  async createReferral(request: CreateReferralRequest): Promise<CreateReferralResponse> {
    return this.executeWithRetry(async () => {
      try {
        const response = await this.apiClient.post<CreateReferralResponse>('/referrals/', {
          patient_email: request.patientEmail,
          patient_name: request.patientName,
          notes: request.notes,
          expires_in_days: request.expiresInDays || 30,
          metadata: request.metadata,
        });

        return response;
      } catch (error) {
        const httpError = error as { status?: number };
        throw this.createError(
          'createReferral',
          error instanceof Error ? error.message : 'Failed to create referral',
          httpError.status
        );
      }
    });
  }

  /**
   * Claim a referral using token
   *
   * @param request - Referral claim request data
   * @returns Promise resolving to referral claim response
   */
  async claimReferral(request: ClaimReferralRequest): Promise<ClaimReferralResponse> {
    return this.executeWithRetry(async () => {
      try {
        const response = await this.apiClient.post<ClaimReferralResponse>('/referrals/claim', {
          referral_token: request.token,
          patient_id: request.patientId,
          patient_email: request.patientEmail,
          patient_name: request.patientName,
        });

        // Store successful claim in local storage for persistence
        if (response.status === 'OK' && response.referral) {
          const storageResult = storageManager.setItem(REFERRAL_STORAGE.TOKEN, request.token);
          if (!storageResult.success) {
            this.logger.warn('Failed to store referral token:', storageResult.error);
          }
        }

        return response;
      } catch (error) {
        const httpError = error as { status?: number };
        throw this.createError(
          'claimReferral',
          error instanceof Error ? error.message : 'Failed to claim referral',
          httpError.status
        );
      }
    });
  }

  /**
   * Validate a referral token
   *
   * @param token - Referral token to validate
   * @returns Promise resolving to referral validation response
   */
  async validateReferral(token: string): Promise<ValidateReferralResponse> {
    return this.executeWithDeduplication(`validate-${token}`, async () => {
      try {
        const response = await this.apiClient.get<ValidateReferralResponse>(`/referrals/validate/${token}`);
        return response;
      } catch (error) {
        const httpError = error as { status?: number };
        throw this.createError(
          'validateReferral',
          error instanceof Error ? error.message : 'Failed to validate referral',
          httpError.status
        );
      }
    });
  }

  /**
   * Get referral by ID
   *
   * @param referralId - Referral ID
   * @returns Promise resolving to referral data
   */
  async getReferral(referralId: string): Promise<Referral> {
    return this.executeWithDeduplication(`referral-${referralId}`, async () => {
      try {
        const response = await this.apiClient.get<Referral>(`/referrals/${referralId}`);
        this.logger.debug('response.referral: ', response);
        return response;
      } catch (error) {
        const httpError = error as { status?: number };
        throw this.createError(
          'getReferral',
          error instanceof Error ? error.message : 'Failed to get referral',
          httpError.status
        );
      }
    });
  }

  /**
   * Get referral by token
   *
   * @param token - Referral token
   * @returns Promise resolving to referral data
   */
  async getReferralByToken(token: string): Promise<Referral> {
    return this.executeWithDeduplication(`referral-token-${token}`, async () => {
      try {
        const response = await this.apiClient.get<Referral>(`/referrals/token/${token}`);
        return response;
      } catch (error) {
        const httpError = error as { status?: number };
        throw this.createError(
          'getReferralByToken',
          error instanceof Error ? error.message : 'Failed to get referral by token',
          httpError.status
        );
      }
    });
  }

  /**
   * List referrals with filtering and pagination
   *
   * @param request - List referrals request parameters
   * @returns Promise resolving to referrals list response
   */
  async listReferrals(request: ListReferralsRequest = {}): Promise<ListReferralsResponse> {
    // Create a unique key based on request parameters for deduplication
    const requestKey = JSON.stringify({
      status: request.status,
      providerId: request.providerId,
      patientId: request.patientId,
      limit: request.limit,
      offset: request.offset,
      sortBy: request.sortBy,
      sortOrder: request.sortOrder
    });

    return this.executeWithDeduplication(`listReferrals-${requestKey}`, async () => {
      return this.executeWithRetry(async () => {
        try {
          const params = new URLSearchParams();

          if (request.status) params.append('status', request.status);
          if (request.providerId) params.append('provider_id', request.providerId);
          if (request.patientId) params.append('patient_id', request.patientId);
          if (request.limit) params.append('limit', request.limit.toString());
          if (request.offset) params.append('offset', request.offset.toString());
          if (request.sortBy) params.append('sort_by', request.sortBy);
          if (request.sortOrder) params.append('sort_order', request.sortOrder);

          const url = `/referrals/${params.toString() ? `?${params.toString()}` : ''}`;
          const response = await this.apiClient.get<ListReferralsResponse>(url);

          return response;
        } catch (error) {
          const httpError = error as { status?: number };
          throw this.createError(
            'listReferrals',
            error instanceof Error ? error.message : 'Failed to list referrals',
            httpError.status
          );
        }
      });
    });
  }

  /**
   * Update referral
   *
   * @param referralId - Referral ID
   * @param request - Update request data
   * @returns Promise resolving to update response
   */
  async updateReferral(referralId: string, request: UpdateReferralRequest): Promise<UpdateReferralResponse> {
    return this.executeWithRetry(async () => {
      try {
        const response = await this.apiClient.put<UpdateReferralResponse>(`/referrals/${referralId}`, {
          status: request.status,
          notes: request.notes,
          metadata: request.metadata,
        });

        return response;
      } catch (error) {
        const httpError = error as { status?: number };
        throw this.createError(
          'updateReferral',
          error instanceof Error ? error.message : 'Failed to update referral',
          httpError.status
        );
      }
    });
  }

  /**
   * Delete referral
   *
   * @param referralId - Referral ID
   * @returns Promise resolving when deletion is complete
   */
  async deleteReferral(referralId: string): Promise<void> {
    return this.executeWithRetry(async () => {
      try {
        await this.apiClient.delete(`/referrals/${referralId}`);
      } catch (error) {
        const httpError = error as { status?: number };
        throw this.createError(
          'deleteReferral',
          error instanceof Error ? error.message : 'Failed to delete referral',
          httpError.status
        );
      }
    });
  }

  /**
   * Upload a document for a referral
   *
   * @param request - Document upload request data
   * @returns Promise resolving to document upload response
   */
  async uploadDocument(request: UploadDocumentRequest): Promise<ReferralDocument> {
    return this.executeWithRetry(async () => {
      try {
        const response = await this.apiClient.post<ReferralDocument>('/referral-documents/', {
          referral_id: request.referralId,
          file_data: request.fileData,
          filename: request.filename,
          mime_type: request.mimeType,
          description: request.description,
        });

        return response;
      } catch (error) {
        const httpError = error as { status?: number };
        throw this.createError(
          'uploadDocument',
          error instanceof Error ? error.message : 'Failed to upload document',
          httpError.status
        );
      }
    });
  }

  /**
   * Get document details by ID
   *
   * @param documentId - Document ID
   * @returns Promise resolving to document data
   */
  async getDocument(documentId: string): Promise<ReferralDocument> {
    return this.executeWithDeduplication(`document-${documentId}`, async () => {
      try {
        const response = await this.apiClient.get<ReferralDocument>(`/referral-documents/${documentId}`);
        return response;
      } catch (error) {
        const httpError = error as { status?: number };
        throw this.createError(
          'getDocument',
          error instanceof Error ? error.message : 'Failed to get document details',
          httpError.status
        );
      }
    });
  }

  /**
   * Download document file
   *
   * @param documentId - Document ID
   * @returns Promise resolving to file blob
   */
  async downloadDocument(documentId: string): Promise<Blob> {
    return this.executeWithDeduplication(`download-${documentId}`, async () => {
      try {
        // Use custom fetch for blob response since ApiClient doesn't support it
        const config = this.apiClient.getConfig();
        const url = `${config.baseUrl}/referral-documents/${documentId}/download`;

        const response = await fetch(url, {
          method: 'GET',
          headers: config.headers,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        return await response.blob();
      } catch (error) {
        const httpError = error as { status?: number };
        throw this.createError(
          'downloadDocument',
          error instanceof Error ? error.message : 'Failed to download document',
          httpError.status
        );
      }
    });
  }

  /**
   * Delete a document
   *
   * @param documentId - Document ID
   * @returns Promise resolving to deletion response
   */
  async deleteDocument(documentId: string): Promise<DeleteDocumentResponse> {
    return this.executeWithRetry(async () => {
      try {
        const response = await this.apiClient.delete<DeleteDocumentResponse>(`/referral-documents/${documentId}`);
        return response;
      } catch (error) {
        const httpError = error as { status?: number };
        throw this.createError(
          'deleteDocument',
          error instanceof Error ? error.message : 'Failed to delete document',
          httpError.status
        );
      }
    });
  }

  /**
   * Get all documents for a specific referral
   *
   * @param referralId - Referral ID
   * @param request - List documents request parameters
   * @returns Promise resolving to documents list response
   */
  async getDocumentsByReferral(referralId: string, request: ListDocumentsRequest = {}): Promise<ListDocumentsResponse> {
    return this.executeWithDeduplication(`referral-docs-${referralId}-${request.page || 1}-${request.perPage || 20}`, async () => {
      try {
        const params = new URLSearchParams();

        if (request.page) params.append('page', request.page.toString());
        if (request.perPage) params.append('per_page', request.perPage.toString());

        const url = `/referral-documents/referral/${referralId}${params.toString() ? `?${params.toString()}` : ''}`;
        const response = await this.apiClient.get<ListDocumentsResponse>(url);

        return response;
      } catch (error) {
        const httpError = error as { status?: number };
        throw this.createError(
          'getDocumentsByReferral',
          error instanceof Error ? error.message : 'Failed to get documents for referral',
          httpError.status
        );
      }
    });
  }

  /**
   * Get all documents uploaded by the current user
   *
   * @param request - List documents request parameters
   * @returns Promise resolving to documents list response
   */
  async getUserUploadedDocuments(request: ListDocumentsRequest = {}): Promise<ListDocumentsResponse> {
    return this.executeWithDeduplication(`user-uploads-${request.page || 1}-${request.perPage || 20}`, async () => {
      try {
        const params = new URLSearchParams();

        if (request.page) params.append('page', request.page.toString());
        if (request.perPage) params.append('per_page', request.perPage.toString());

        const url = `/referral-documents/user/uploads${params.toString() ? `?${params.toString()}` : ''}`;
        const response = await this.apiClient.get<ListDocumentsResponse>(url);

        return response;
      } catch (error) {
        const httpError = error as { status?: number };
        throw this.createError(
          'getUserUploadedDocuments',
          error instanceof Error ? error.message : 'Failed to get user uploaded documents',
          httpError.status
        );
      }
    });
  }

  /**
   * Update document description
   *
   * @param documentId - Document ID
   * @param request - Update description request data
   * @returns Promise resolving to update response
   */
  async updateDocumentDescription(documentId: string, request: UpdateDocumentDescriptionRequest): Promise<UpdateDocumentDescriptionResponse> {
    return this.executeWithRetry(async () => {
      try {
        const response = await this.apiClient.patch<UpdateDocumentDescriptionResponse>(`/referral-documents/${documentId}/description`, {
          description: request.description,
        });

        return response;
      } catch (error) {
        const httpError = error as { status?: number };
        throw this.createError(
          'updateDocumentDescription',
          error instanceof Error ? error.message : 'Failed to update document description',
          httpError.status
        );
      }
    });
  }

  /**
   * Submit a referral (legacy compatibility method)
   *
   * @param patientData - Patient information
   * @param selectedDietitian - Selected dietitian profile
   * @returns Promise resolving to submission result
   */
  async submitReferral(
    patientData: Partial<PatientData>,
    selectedDietitian: DietitianProfile
  ): Promise<ReferralSubmissionResult> {
    return this.executeWithRetry(async () => {
      try {
        // Transform data to API format
        const requestData = this.transformToApiRequest(patientData, selectedDietitian);

        // Use existing createReferral method with transformed data
        const response = await this.apiClient.post<ReferralSubmissionResponse>('/referrals/', requestData);

        // Transform response to legacy format
        return this.transformFromApiResponse(response);
      } catch (error) {
        // Handle errors in legacy format
        return this.handleSubmissionError(error);
      }
    });
  }

  /**
   * Transform patient data and dietitian selection to API request format
   *
   * @private
   * @param patientData - Patient information
   * @param selectedDietitian - Selected dietitian profile
   * @returns Transformed API request data
   */
  private transformToApiRequest(
    patientData: Partial<PatientData>,
    selectedDietitian: DietitianProfile
  ): ReferralApiRequest {
    return {
      patient_name: patientData.patientName || '',
      email: patientData.email || '',
      conditions: patientData.conditions || [],
      additional_notes: patientData.additionalNotes || '',
      dietitian_id: selectedDietitian.id
    };
  }

  /**
   * Transform API response to legacy submission result format
   *
   * @private
   * @param response - API response
   * @returns Legacy submission result
   */
  private transformFromApiResponse(response: ReferralSubmissionResponse): ReferralSubmissionResult {
    return {
      referralId: response.id || '',
      status: 'success',
      message: 'Referral submitted successfully',
      submissionDate: response.created_at || new Date().toISOString(),
      patientName: response.patient_name || '',
      dietitianName: response.dietitian_name || '',
      expiresAt: response.expires_at
    };
  }

  /**
   * Handle submission errors in legacy format
   *
   * @private
   * @param error - Error that occurred during submission
   * @returns Legacy error result
   */
  private handleSubmissionError(error: unknown): ReferralSubmissionResult {
    this.logger.error('Referral submission error:', error);

    if (error instanceof ReferralApiError) {
      return {
        referralId: '',
        status: 'error',
        message: error.message,
        submissionDate: new Date().toISOString(),
        fieldErrors: error.fieldErrors
      };
    }

    // Handle unexpected errors
    return {
      referralId: '',
      status: 'error',
      message: 'An unexpected error occurred. Please try again.',
      submissionDate: new Date().toISOString()
    };
  }

  /**
   * Get stored referral token from local storage
   *
   * @returns Stored referral token or null
   */
  getStoredReferralToken(): string | null {
    const result = storageManager.getItem<string>(REFERRAL_STORAGE.TOKEN);
    return result.success ? result.data || null : null;
  }

  /**
   * Clear stored referral token from local storage
   *
   * @returns Whether the operation was successful
   */
  clearStoredReferralToken(): boolean {
    const result = storageManager.removeItem(REFERRAL_STORAGE.TOKEN);
    return result.success;
  }

  /**
   * Store referral workflow data
   *
   * @param workflowData - Workflow data to store
   * @returns Whether the operation was successful
   */
  storeWorkflowData(workflowData: Record<string, unknown>): boolean {
    const result = storageManager.setItem(REFERRAL_STORAGE.WORKFLOW, workflowData);
    return result.success;
  }

  /**
   * Get stored referral workflow data
   *
   * @returns Stored workflow data or null
   */
  getStoredWorkflowData(): Record<string, unknown> | null {
    const result = storageManager.getItem<Record<string, unknown>>(REFERRAL_STORAGE.WORKFLOW);
    return result.success ? result.data || null : null;
  }

  /**
   * Clear stored referral workflow data
   *
   * @returns Whether the operation was successful
   */
  clearStoredWorkflowData(): boolean {
    const result = storageManager.removeItem(REFERRAL_STORAGE.WORKFLOW);
    return result.success;
  }

  /**
   * Get domain name for error messages
   *
   * @protected
   * @returns Domain name
   */
  protected getDomainName(): string {
    return 'Referral';
  }

  /**
   * Create domain-specific error instance
   *
   * @protected
   * @param operation - The operation that failed
   * @param message - Error message
   * @param status - HTTP status code
   * @param fieldErrors - Field-specific validation errors
   * @param code - Error code for programmatic handling
   * @returns ReferralApiError instance
   */
  protected createDomainError(
    operation: string,
    message: string,
    status?: number,
    fieldErrors?: Record<string, string>,
    code?: string
  ): ReferralApiError {
    const formattedMessage = `${operation}: ${message}`;
    return new ReferralApiError(formattedMessage, status, fieldErrors, code);
  }
}

/**
 * Default ReferralDomain instance
 */
export const referralDomain = new ReferralDomain();